import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { Icon } from 'assets/icons';
import { useContentJobService } from 'hooks/api/agents/content/content-job';
import { addOneContentJobToStore } from 'store/actions/content-job';
import {
  AGENT_TYPE_LABELS,
  READ_ONLY_STATUSES,
  STATUS_CLASS,
  STATUS_LABELS,
  formatDateTime,
  resolveJobOutput,
  resolveJobVersion,
  resolveLatestRejection
} from '../config/job';
import { EmailJobReview } from './email';
import { SocialJobReview } from './social';
import { WhatsappJobReview } from './whatsapp';
import { JobVersionHistory } from './versions';
import { ContentJobRejectModal } from './rejection';
import styles from './view.module.css';

const POLL_INTERVAL = 8000;

export const ContentJobViewPage = ({ job_id, refresh_key = 0 }) => {
  const dispatch = useDispatch();
  const {
    acceptContentJob,
    fetchContentJob,
    rejectContentJob,
    triggerContentAgentRun,
    updateContentJobOutput
  } = useContentJobService();

  const [content_job, setContentJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null);
  const [show_reject_modal, setShowRejectModal] = useState(false);
  const active_job_id_ref = useRef(job_id);

  useEffect(() => {
    active_job_id_ref.current = job_id;
    setContentJob(null);
    setActing(null);
    setShowRejectModal(false);
    if (job_id) loadContentJob(job_id, { show_loader: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job_id]);

  useEffect(() => {
    if (!refresh_key || !job_id) return;
    loadContentJob(job_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh_key]);

  useEffect(() => {
    if (content_job?.status !== 'regenerating') return;

    const poller = setInterval(() => loadContentJob(job_id), POLL_INTERVAL);
    return () => clearInterval(poller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content_job?.status, job_id]);

  const loadContentJob = async (id, { show_loader = false } = {}) => {
    if (show_loader) setLoading(true);
    const { content_job: latest_job } = await fetchContentJob(id);

    if (latest_job && active_job_id_ref.current === id) {
      setContentJob(latest_job);
      dispatch(addOneContentJobToStore(latest_job));
    }

    if (show_loader) setLoading(false);
    return latest_job;
  };

  const handleApprove = async (output) => {
    const target_job_id = job_id;
    setActing('approve');

    const has_edits = !!output;
    const { success } = has_edits
      ? await updateContentJobOutput(target_job_id, output, true)
      : await acceptContentJob(target_job_id);

    if (success && active_job_id_ref.current === target_job_id) {
      await loadContentJob(target_job_id);
    }

    setActing(null);
  };

  const handleSaveDraft = async (output) => {
    const target_job_id = job_id;
    setActing('save');

    const { success } = await updateContentJobOutput(target_job_id, output, false);

    if (success && active_job_id_ref.current === target_job_id) {
      await loadContentJob(target_job_id);
    }

    setActing(null);
  };

  const handleRejectSubmit = async (rejection_comment) => {
    const target_job_id = job_id;
    setActing('reject');

    const success = await rejectContentJob(target_job_id, rejection_comment);

    if (success && active_job_id_ref.current === target_job_id) {
      await loadContentJob(target_job_id);
    }

    setActing(null);
    return success;
  };

  const handleRerun = async () => {
    const target_job_id = job_id;
    setActing('rerun');

    const success = await triggerContentAgentRun(content_job.agent_type, content_job.blog_id);

    if (success && active_job_id_ref.current === target_job_id) {
      await loadContentJob(target_job_id);
    }

    setActing(null);
  };

  if (loading || !content_job) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.loadingPane}>
          <Skeleton height={16} width="45%" style={{ marginBottom: 12 }} borderRadius={4} />
          <Skeleton height={28} width="80%" style={{ marginBottom: 20 }} borderRadius={4} />
          <Skeleton height={120} style={{ marginBottom: 12 }} borderRadius={8} />
          <Skeleton height={120} borderRadius={8} />
        </div>
      </div>
    );
  }

  const { agent_type, status, current_version, versions = [], error } = content_job;
  const output = resolveJobOutput(content_job);
  const current = resolveJobVersion(content_job);
  const latest_rejection = resolveLatestRejection(content_job);
  const read_only = READ_ONLY_STATUSES.includes(status);
  const is_regenerating = status === 'regenerating';

  const review_props = {
    content_job,
    output,
    read_only,
    acting,
    onApprove: handleApprove,
    onSaveDraft: handleSaveDraft,
    onReject: () => setShowRejectModal(true)
  };

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.meta}>
            <span className={`${styles.agentBadge} ${styles[`agent_${agent_type}`]}`}>
              {AGENT_TYPE_LABELS[agent_type] || agent_type}
            </span>
            <span
              className={`${styles.statusBadge} ${styles[STATUS_CLASS[status] || 'statusPending']}`}
            >
              {STATUS_LABELS[status] || status}
            </span>
            <span className={styles.metaItem}>
              <span className={styles.metaDot} />v{current_version}
            </span>
            {current?.generated_at && (
              <span className={styles.metaItem}>
                <span className={styles.metaDot} />
                {formatDateTime(current.generated_at)}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className={styles.body}>
        {status === 'needs_manual_intervention' && (
          <div className={`${styles.banner} ${styles.bannerWarning}`}>
            <Icon name="warning" width={18} height={18} />
            <div>
              <p className={styles.bannerTitle}>This job hit 5 rejections</p>
              <p className={styles.bannerText}>
                Auto-regeneration has stopped. Edit the draft by hand, or re-run the agent from
                scratch.
              </p>
            </div>
          </div>
        )}

        {status === 'generation_failed' && (
          <div className={`${styles.banner} ${styles.bannerError}`}>
            <Icon name="cancel" width={18} height={18} />
            <div>
              <p className={styles.bannerTitle}>Generation failed</p>
              <p className={styles.bannerText}>{error || 'The agent did not return a draft.'}</p>
              <button className={styles.bannerBtn} onClick={handleRerun} disabled={!!acting}>
                {acting === 'rerun' ? 'Starting…' : 'Re-run agent'}
              </button>
            </div>
          </div>
        )}

        {status === 'approved' && (
          <div className={`${styles.banner} ${styles.bannerError}`}>
            <Icon name="warning" width={18} height={18} />
            <div>
              <p className={styles.bannerTitle}>Approved, but the send did not go through</p>
              <p className={styles.bannerText}>{error || 'Approve again to retry the send.'}</p>
            </div>
          </div>
        )}

        {latest_rejection && status !== 'regenerating' && (
          <div className={`${styles.banner} ${styles.bannerInfo}`}>
            <Icon name="note" width={18} height={18} />
            <div>
              <p className={styles.bannerTitle}>
                Regenerated after v{latest_rejection.version} was rejected
              </p>
              <p className={styles.bannerText}>“{latest_rejection.rejection_comment}”</p>
            </div>
          </div>
        )}

        {is_regenerating ? (
          <div className={styles.regenerating}>
            <span className={styles.spinner} />
            <p className={styles.regeneratingTitle}>The agent is writing a new version</p>
            <p className={styles.regeneratingHint}>
              This usually takes 20–60 seconds. This screen refreshes on its own.
            </p>
          </div>
        ) : !output ? (
          <div className={styles.emptyOutput}>No draft on this version yet.</div>
        ) : agent_type === 'email' ? (
          <EmailJobReview {...review_props} />
        ) : agent_type === 'whatsapp' ? (
          <WhatsappJobReview {...review_props} />
        ) : (
          <SocialJobReview {...review_props} />
        )}

        <JobVersionHistory versions={versions} current_version={current_version} />
      </div>

      <ContentJobRejectModal
        show={show_reject_modal}
        submitting={acting === 'reject'}
        onClose={() => setShowRejectModal(false)}
        onSubmit={handleRejectSubmit}
      />
    </div>
  );
};
